import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { asyncConnect } from 'redux-connect';
import Helmet from 'react-helmet';
import moment from 'moment';
import { push } from 'connected-react-router';
import { Link } from 'react-router-dom';
import { fromJS } from 'immutable';
import { loadOwnCredits, load as loadAuth } from 'redux/modules/auth';
import { setSuccessMessage } from 'redux/modules/successMessage';
import {
  isBriefLoaded,
  loadBrief,
  loadCreativePitch,
  loadPendingPitches,
  loadAcceptedPitch,
  sendPitch,
  updatePitch,
  deleteBrief,
  declinePitch,
  removeSuggested as removeCreativeFromList,
  inviteUser,
  loadQuestions,
  loadOwnQuestions,
  updateQuestionText,
  postQuestion,
  connectPayPal,
  getPaymentToken,
  addVoucher,
  resetBriefDetails,
  resendContactDetails,
} from 'redux/modules/briefDetails';
import { generatePitch } from 'redux/modules/pitchWriter';
import { rate } from 'redux/modules/rating';
import { loadMyPitches } from 'redux/modules/pitches';
import { loadCredits } from 'redux/modules/portfolio';
import { loadPartners } from 'redux/modules/partners';
import { load as loadSuggestedRoles } from 'redux/modules/suggestedRoles';
import { saveJob, unsaveJob } from 'redux/modules/projects2';
import { createSubscriptionTransaction } from 'redux/modules/subscriptions';
import {
  get,
  beginsWithVowel,
  toSeoUrl,
  isCreativeJob,
} from 'helpers/HelperFunctions';
// import { featuredRolesMatchJobRole } from 'helpers/RoleMatching';
import {
  Alert,
  Button,
  Col,
  Glyphicon,
  Grid,
  Radio,
  Row,
} from 'react-bootstrap';

import AlertBox from 'components/AlertBox/AlertBox';
import Breadcrumbs from 'components/Shared/Breadcrumbs';
import BriefDetail from 'components/BriefDetail/BriefDetail';
import CreativeAcceptedPitch from 'components/CreativeAcceptedPitch/CreativeAcceptedPitch';
import CreativeSubmittedPitch from 'components/CreativeSubmittedPitch/CreativeSubmittedPitch';
import CreativePitch from 'components/BriefDetailsFreelancer/CreativePitch/CreativePitch';
import Icon from 'components/Icon/Icon';
import Loader from 'components/Loader/Loader';
import Modal from 'components/Modal/Modal';
import Panel from 'components/Panel/Panel';
import Pitch from 'components/Pitch/Pitch/Pitch';
import PublicQuestions from 'components/BriefDetailsFreelancer/PublicQuestions';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import ProjectSelector from 'components/Shared/Selectors/ProjectSelector/ProjectSelector';
import RatingModal from 'components/Rating/RatingModal';

import styles from './BriefDetailsFreelancer.module.scss';
import { trackEvent } from 'helpers/Analytics';
import PortfolioAdverts from 'components/Portfolio/PortfolioAbout/components/ChildComponents/Adverts/PortfolioAdverts';
import BriefDetailsSignUp from 'components/BriefDetailsSignUp/BriefDetailsSignUp';
import ExpiredBriefDetailsSignUp from 'components/ExpiredBriefDetailsSignUp/ExpiredBriefDetailsSignUp';
import BriefDetailsHowItWorks from 'components/BriefDetailsHowItWorks/BriefDetailsHowItWorks';
import RelatedJobs from 'components/RelatedJobs/RelatedJobs';
import cookie from 'react-cookie';
import { RECENT_JOBS_COOKIE } from 'constants/constants';
import {
  maybeLogSubscriptionTransaction,
  resolveSubscriptionLogJobId,
} from 'helpers/maybeLogProSubscriptionTransaction';
import { maybeTrackAudienceTransitionOnSubscriptionSuccess } from 'helpers/audienceTracking';

const MINIMUM_PITCH_AMOUNT = 2500;
const MINIMUM_INITIAL_PITCH_AMOUNT = MINIMUM_PITCH_AMOUNT / 100;
const MAX_RECENT_JOBS = 5;

const manageRecentJobsCookie = (jobId, jobUrl) => {
  let recentJobs = cookie.load(RECENT_JOBS_COOKIE) || [];

  const newJob = {
    id: jobId,
    url: jobUrl,
  };

  // Remove the current job if it already exists in the array
  recentJobs = recentJobs.filter((job) => job.id !== jobId);

  recentJobs.unshift(newJob);

  if (recentJobs.length > MAX_RECENT_JOBS) {
    recentJobs = recentJobs.slice(0, MAX_RECENT_JOBS);
  }

  // Set cookie to expire in 30 days
  const thirtyDaysInMilliseconds = 30 * 24 * 60 * 60 * 1000;
  const expiryDate = new Date(Date.now() + thirtyDaysInMilliseconds);
  cookie.save(RECENT_JOBS_COOKIE, recentJobs, {
    path: '/',
    expires: expiryDate,
  });

  return recentJobs;
};

@asyncConnect(
  [
    {
      promise: ({
        store: { dispatch, getState },
        match: {
          params: { id },
        },
      }) => {
        const promises = [];

        const encodedId = id ? id.split('-')[0] : null;

        // As we get by encoded ID, just always get the brief
        if (encodedId && !isBriefLoaded(getState())) {
          promises.push(dispatch(loadBrief(encodedId)));
        }

        return Promise.all(promises);
      },
    },
  ],
  (state) => ({
    user: state.auth.user,
    brief: state.briefDetails.brief,
    briefEntities: state.entities.briefs,
    creativePitch: state.briefDetails.creativePitch,
    deletingBrief: state.briefDetails.deletingBrief,
    generatedPitch: state.pitchWriter.generatedPitch,
    introsLeft: state.messages.introsLeft,
    introsLoading: state.messages.introsLoading,
    applicationCreditsRemaining: state.messages.applicationCreditsRemaining,
    willUseCredits: state.messages.willUseCredits,
    loadedBrief: state.briefDetails.loadedBrief,
    loadedCreativePitch: state.briefDetails.loadedCreativePitch,
    loadedPartners: state.partners.loadedPartners,
    loadedPaymentToken: state.briefDetails.loadedPaymentToken,
    loadedQuestions: state.briefDetails.loadedQuestions,
    loadedOwnQuestions: state.briefDetails.loadedOwnQuestions,
    loadingCreativePitch: state.briefDetails.loadingCreativePitch,
    loadingPartners: state.partners.loadingPartners,
    loadingOwnerAcceptedPitch: state.briefDetails.loadingOwnerAcceptedPitch,
    loadingGeneratedPitch: state.pitchWriter.loadingGeneratedPitch,
    loadingQuestions: state.briefDetails.loadingQuestions,
    loadingOwnQuestions: state.briefDetails.loadingOwnQuestions,
    moreQuestions: state.briefDetails.moreQuestions,
    ownerAcceptedPitch: state.briefDetails.ownerAcceptedPitch,
    ownerPendingPitches: state.briefDetails.ownerPendingPitches,
    paymentToken: state.briefDetails.paymentToken,
    partners: state.partners.partners,
    portfolio: fromJS(state.portfolio),
    postingQuestion: state.briefDetails.postingQuestion,
    publicQuestionEntities: state.entities.publicQuestions,
    questionPagesLoaded: state.briefDetails.questionPagesLoaded,
    questions: state.briefDetails.questions,
    ownQuestions: state.briefDetails.ownQuestions,
    questionText: state.briefDetails.questionText,
    sendingPitch: state.briefDetails.sendingPitch,
    sendPitchRoleMismatchError: state.briefDetails.sendPitchRoleMismatchError,
    totalPendingPitches: state.briefDetails.totalPendingPitches,
    updatingBrief: state.briefDetails.updatingBrief,
    voucherEntities: state.entities.vouchers,
    addingVoucher: state.briefDetails.addingVoucher,
    updatingPitch: state.briefDetails.updatingPitch,
    ownCredits: state.auth.ownCredits,
    entities: state.entities,
    myPitches: state.pitches.myPitches,
    suggestedRoles: state.suggestedRoles.suggestedRoles,
    loadingSuggestedRoles: state.suggestedRoles.loading,
    loadedSuggestedRoles: state.suggestedRoles.loaded,
  }),
  {
    addVoucher,
    connectPayPal,
    declinePitch,
    deleteBrief,
    generatePitch,
    getPaymentToken,
    inviteUser,
    loadAcceptedPitch,
    loadCreativePitch,
    loadCredits,
    loadOwnCredits,
    loadOwnQuestions,
    loadPartners,
    loadPendingPitches,
    loadQuestions,
    postQuestion,
    pushState: push,
    rate,
    removeCreativeFromList,
    resendContactDetails,
    resetBriefDetails,
    sendPitch,
    setSuccessMessage,
    updatePitch,
    updateQuestionText,
    loadMyPitches,
    loadSuggestedRoles,
    loadAuth,
    saveJob,
    unsaveJob,
    createSubscriptionTransaction,
  }
)
export default class BriefDetailsFreelancer extends Component {
  static propTypes = {
    answerQuestion: PropTypes.func,
    brief: PropTypes.number,
    briefEntities: PropTypes.object,
    user: PropTypes.object,
    userEntities: PropTypes.object,
    connectPayPal: PropTypes.func,
    creativePitch: PropTypes.object,
    declinePitch: PropTypes.func,
    deleteBrief: PropTypes.func,
    deletingBrief: PropTypes.bool,
    encodedId: PropTypes.string,
    entities: PropTypes.object,
    generatePitch: PropTypes.func,
    generatedPitch: PropTypes.string,
    getPaymentToken: PropTypes.func,
    history: PropTypes.object,
    introsLeft: PropTypes.number,
    introsLoading: PropTypes.bool,
    applicationCreditsRemaining: PropTypes.number,
    willUseCredits: PropTypes.bool,
    inviteUser: PropTypes.func,
    loadAcceptedPitch: PropTypes.func,
    loadCreativePitch: PropTypes.func,
    loadCredits: PropTypes.func,
    loadPartners: PropTypes.func,
    loadedBrief: PropTypes.bool,
    loadedCreativePitch: PropTypes.bool,
    loadedPartners: PropTypes.bool,
    loadedPaymentToken: PropTypes.bool,
    loadedQuestions: PropTypes.bool,
    loadedOwnQuestions: PropTypes.bool,
    loadingCreativePitch: PropTypes.bool,
    loadingGeneratedPitch: PropTypes.bool,
    loadingIndividualQuestion: PropTypes.bool,
    loadingPartners: PropTypes.bool,
    loadingOwnerAcceptedPitch: PropTypes.bool,
    loadingQuestions: PropTypes.bool,
    loadingOwnQuestions: PropTypes.bool,
    loadPendingPitches: PropTypes.func,
    loadQuestion: PropTypes.func,
    loadQuestions: PropTypes.func,
    loadOwnCredits: PropTypes.func,
    loadOwnQuestions: PropTypes.func,
    location: PropTypes.object,
    moreQuestions: PropTypes.bool,
    ownerAcceptedPitch: PropTypes.object,
    ownerPendingPitches: PropTypes.array,
    ownCredits: PropTypes.object,
    match: PropTypes.object,
    paymentToken: PropTypes.string,
    portfolio: PropTypes.object,
    postingQuestion: PropTypes.bool,
    postQuestion: PropTypes.func,
    publicQuestionEntities: PropTypes.object,
    pushState: PropTypes.func,
    questionPagesLoaded: PropTypes.number,
    questions: PropTypes.array,
    ownQuestions: PropTypes.array,
    questionText: PropTypes.string,
    rate: PropTypes.func,
    removeCreativeFromList: PropTypes.func,
    resendContactDetails: PropTypes.func,
    resetBriefDetails: PropTypes.func,
    sendingPitch: PropTypes.bool,
    sendPitch: PropTypes.func,
    updatePitch: PropTypes.func,
    totalPendingPitches: PropTypes.number,
    updateAnswerText: PropTypes.func,
    updatedAnswerText: PropTypes.object,
    updateQuestionText: PropTypes.func,
    updatingBrief: PropTypes.bool,
    voucherEntities: PropTypes.object,
    addVoucher: PropTypes.func,
    addingVoucher: PropTypes.bool,
    setSuccessMessage: PropTypes.func,
    updatingPitch: PropTypes.bool,
    loadMyPitches: PropTypes.func,
    myPitches: PropTypes.object,
    partners: PropTypes.array,
    saveJob: PropTypes.func,
    unsaveJob: PropTypes.func,
    createSubscriptionTransaction: PropTypes.func,
    loadSuggestedRoles: PropTypes.func,
    suggestedRoles: PropTypes.array,
    loadingSuggestedRoles: PropTypes.bool,
    loadedSuggestedRoles: PropTypes.bool,
    sendPitchRoleMismatchError: PropTypes.string,
  };

  constructor(props) {
    super(props);

    const brief = this.getBrief();

    this.state = {
      availability:
        props.user &&
        props.user.available_at &&
        moment().isBefore(props.user.available_at)
          ? props.user.available_at
          : null,
      availabilityToggle:
        props.user &&
        props.user.available_at &&
        moment().isBefore(props.user.available_at)
          ? 1
          : 0,
      dayRateCurrency:
        props.user && props.user.day_rate_currency
          ? props.user.day_rate_currency
          : 'usd',
      dayRate: props.user && props.user.day_rate ? props.user.day_rate : 0,
      isSaved: (brief && brief.saved_by_current_user) || false,
      paymentVerificationRequired: true,
      showCompleteAndRateConfirmation: false,
      showDeleteConfirmation: false,
      deleteReasonId: 0,
      showPaymentConfirmation: false,
      showPitchSubmittedModal: false,
      showRatingDialog: false,
      showShareDialog: false,
      showRelevantProjectsSelector: false,

      pitchAmount: null,
      pitchDeposit: 80,
      pitchError: {},
      pitchText: '',
      pitchDays: null,

      editingPitch: false,
      editingPitchAmount: null,
      editingPitchDays: null,
      editingPitchDeposit: 0,
      editingPitchText: '',

      pitchAgain: false,

      askingQuestion: false,

      editingBrief: false,

      selectedRelevantProjects: [],

      locationCheckbox: false,

      confirmationCheckbox: { terms: false },

      shareResumeCheckbox: props.user && props.user.has_resume ? true : false,

      testingPartners: props.location.query.testing ?? false,
    };
  }

  componentDidMount() {
    const {
      history,
      match,
      location,
      loadedQuestions,
      loadingQuestions,
      loadQuestions,
      loadCredits,
      loadPartners,
      loadAcceptedPitch,
      loadCreativePitch,
      loadedOwnQuestions,
      loadingOwnQuestions,
      loadOwnQuestions,
      user,
    } = this.props;

    const brief = this.getBrief();
    if (brief && brief.saved_by_current_user !== undefined) {
      this.setState({
        isSaved: brief.saved_by_current_user,
      });
    }

    const briefId = this.getBrief() ? this.getBrief().id : false;

    if (!user && location.query.utm_source === 'email') {
      history.push(
        `/signin?referer=${encodeURIComponent(`/projects/${match.params.id}`)}`
      );
    }

    if (!loadedQuestions && !loadingQuestions && briefId) {
      loadQuestions(
        briefId,
        user && this.getOwner() && user.id === this.getOwner().id ? null : false
      );
    }

    loadPartners('external', this.state.testingPartners);

    if (user) {
      if (this.getOwner() && user.id === this.getOwner().id && briefId) {
        // The owner
        loadAcceptedPitch(briefId);
      } else if (user && this.getOwner() && user.id !== this.getOwner().id) {
        // A creative
        if (briefId) {
          loadCreativePitch(user.id, briefId);
        }
        loadCredits(user.username, 1, true);
      }

      // load the current users pending questions as they are not loaded by the endpoint above
      if (
        this.getOwner() &&
        user.id !== this.getOwner().id &&
        !loadedOwnQuestions &&
        !loadingOwnQuestions &&
        briefId
      ) {
        loadOwnQuestions(user.id, briefId, true);
      }
    }
    if (briefId) {
      const jobUrl = `/projects/${match.params.id}`;
      manageRecentJobsCookie(briefId, jobUrl);

      trackEvent('user-lands-on-job', {
        jobExternal: this.getBrief().external,
        role: this.getBrief().role,
        skill: this.getBrief().skill,
        jobRemote: this.getBrief().remote,
        jobLocation: this.getBrief().location,
        loggedIn: !!user,
        userType: user ? user.type : 'guest',
        userIsPro: user ? user.pro : 'guest',
        jobMonth: moment(this.getBrief().timestamp).format('YYYY-MM'),
      });
    }

    if (__CLIENT__) {
      this.maybeLogSubscriptionTransactionFromCheckout();
      void maybeTrackAudienceTransitionOnSubscriptionSuccess({
        user: this.props.user,
        locationSearch: this.props.location?.search || '',
        reloadAuth: this.props.loadAuth,
      });
    }

    // Load suggested roles for role-eligibility check if not already loaded
    const { loadSuggestedRoles, loadedSuggestedRoles, loadingSuggestedRoles } =
      this.props;
    if (!loadedSuggestedRoles && !loadingSuggestedRoles) {
      loadSuggestedRoles();
    }
  }

  maybeLogSubscriptionTransactionFromCheckout = () => {
    const { user, location, createSubscriptionTransaction } = this.props;
    const search = location?.search || '';
    const pageJobId = this.getBrief()?.id;
    const resolvedJobId = resolveSubscriptionLogJobId(pageJobId);

    if (user?.id == null || resolvedJobId == null) {
      return;
    }

    const dedupeKey = `${user.id}|${search}|${resolvedJobId}`;
    if (this._proSubscriptionTxLoggedKey === dedupeKey) {
      return;
    }

    const dispatched = maybeLogSubscriptionTransaction({
      user,
      locationSearch: search,
      createSubscriptionTransaction,
      jobId: resolvedJobId,
    });
    if (dispatched) {
      this._proSubscriptionTxLoggedKey = dedupeKey;
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      this.props.sendingPitch &&
      !nextProps.sendingPitch &&
      !this.props.creativePitch &&
      nextProps.creativePitch
    ) {
      this.setState({
        showPitchSubmittedModal: true,
      });
      trackEvent('freelancer-application-submitted', {
        briefId: this.getBrief()?.id,
        role: this.getBrief()?.role,
      });
    }

    if (
      this.props.sendingPitch &&
      !nextProps.sendingPitch &&
      this.state.pitchAgain
    ) {
      this.setState({ pitchAgain: false });
      if (nextProps.creativePitch) {
        trackEvent('freelancer-application-submitted', {
          briefId: this.getBrief()?.id,
          role: this.getBrief()?.role,
        });
      }
    }

    if (this.props.updatingPitch && !nextProps.updatingPitch) {
      this.setState({
        editingPitch: false,
      });
    }

    if (
      __CLIENT__ &&
      typeof sessionStorage !== 'undefined' &&
      typeof sessionStorage[`pitch_${this.props.match.params.id}`] !==
        'undefined'
    ) {
      const savedPitch = JSON.parse(
        sessionStorage[`pitch_${this.props.match.params.id}`]
      );

      this.setState({
        pitchText: savedPitch.pitchText,
        pitchAmount: savedPitch.pitchAmount,
        pitchDeposit: savedPitch.pitchDeposit,
      });
    }

    if (
      this.props.loadingCreativePitch &&
      !nextProps.loadingCreativePitch &&
      !this.props.creativePitch &&
      this.getBrief().amount > 0
    ) {
      this.setState({ pitchAmount: this.getBrief().amount / 100 });
    }

    if (this.props.postingQuestion && !nextProps.postingQuestion) {
      this.setState({
        askingQuestion: false,
      });
    }
  }

  UNSAFE_componentWillUpdate() {
    if (
      this.getBrief() &&
      this.getBrief().approved_at === '' &&
      this.props.match.params.subsection === 'invite'
    ) {
      this.props.history.push(location.pathname.replace('/invite', ''));
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.generatedPitch !== this.props.generatedPitch &&
      this.props.generatedPitch
    ) {
      this.setState((prevState) => ({
        pitchText: this.props.generatedPitch,
        pitchError: Object.assign({}, prevState.pitchError, {
          pitchText: null,
        }),
      }));
    }

    if (!this.getBrief(prevProps) && this.getBrief()) {
      trackEvent('user-lands-on-job', {
        jobExternal: this.getBrief().external,
        role: this.getBrief().role,
        skill: this.getBrief().skill,
        jobRemote: this.getBrief().remote,
        jobLocation: this.getBrief().location,
        loggedIn: !!this.props.user,
        userType: this.props.user ? this.props.user.type : 'guest',
        userIsPro: this.props.user ? this.props.user.pro : 'guest',
        jobMonth: moment(this.getBrief().timestamp).format('YYYY-MM'),
      });
    }

    if (__CLIENT__) {
      const searchChanged =
        prevProps.location?.search !== this.props.location?.search;
      const userChanged = prevProps.user?.id !== this.props.user?.id;
      const briefBecameAvailable = !this.getBrief(prevProps) && this.getBrief();
      if (searchChanged || userChanged || briefBecameAvailable) {
        this.maybeLogSubscriptionTransactionFromCheckout();
      }
    }
  }

  componentWillUnmount() {
    this.props.resetBriefDetails();
  }

  onConnectPayPal = (nonce) => {
    // No user logged in
    if (!this.props.user) {
      return false;
    }

    this.props.connectPayPal(this.props.user, nonce);

    this.setState({
      paymentVerificationRequired: false,
    });
  };

  getBrief = (briefData = this.props) => {
    const { encodedId: propEncodedId } = briefData;
    const id = briefData.match?.params?.id;
    const encodedId = propEncodedId || (id ? id.split('-')[0] : null);

    return get(
      briefData.entities,
      ['briefs', get(briefData.briefs, [encodedId, 'id'])],
      null
    );
  };

  getBriefOld = () => get(this.props.entities, ['briefs', this.props.brief]);

  getOwner = () =>
    this.getBrief()
      ? get(this.props.entities, [
          'users',
          this.getBrief() && this.getBrief().user,
        ])
      : null;

  getOwnerOld = () =>
    this.getBrief()
      ? get(this.props.entities, ['users', this.getBrief().user])
      : null;

  getAcceptHireAB = () =>
    this.getBrief().budget !== 0 &&
    this.props.user &&
    this.props.user.id % 2 === 1
      ? 'b'
      : 'a';

  getPaymentToken = () => {
    // No user logged in
    if (!this.props.user) {
      return false;
    }

    this.props.getPaymentToken(this.props.user, 'braintree');
  };

  getDepositAmounts = () => {
    const deposits = [
      { label: '80%', value: 80 },
      { label: '50%', value: 50 },
    ];

    return deposits;
  };

  getDeleteReasons = () => {
    const reasons = [
      'Found someone elsewhere',
      'Not enough or no pitches',
      'Poor quality pitches',
    ];

    if (this.getBrief().amount && this.getBrief().amount !== 0) {
      reasons.push('Commission rate');
    }

    reasons.push("I'm reposting this brief");
    reasons.push('Other');

    return reasons;
  };

  getInvitedUserIds = () =>
    get(this.getBrief(), ['invites', 'invites'], []).map((inviteEntityId) =>
      get(this.props.entities, ['invites', inviteEntityId, 'user_id'])
    );

  isPageLoaded = () => {
    // No user logged in
    if (!this.props.user) {
      return true;
    }

    return (
      this.props.loadedCreativePitch &&
      (!this.props.creativePitch ||
        (this.props.creativePitch &&
          (this.props.creativePitch.accepted_at === '' ||
            this.props.creativePitch.accepted_at !== '')))
    );
  };

  isBriefRelevant = () =>
    this.isPageLoaded() && (this.getBrief().active || this.props.creativePitch);

  handlePitchUpdate = (pitch, pitchAmount, deposit) => {
    this.props.updatePitch(pitch, pitchAmount, deposit);
  };

  handleDeleteReasonChange = (reasonId) => {
    this.setState({
      deleteReasonId: reasonId,
    });
  };

  handleResendContactDetails = () => {
    this.props.resendContactDetails(this.props.creativePitch.id);
  };

  handleViewPitches = () => {
    document.getElementById('pendingPitches').scrollIntoView();
  };

  handleSendPitch = () => {
    const {
      availability,
      availabilityToggle,
      dayRate,
      dayRateCurrency,
      pitchAmount,
      pitchDeposit,
      pitchError,
      pitchText,
      locationCheckbox,
      confirmationCheckbox,
      shareResumeCheckbox,
    } = this.state;

    if (!this.props.user) {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(
          `pitch_${this.props.match.params.id}`,
          JSON.stringify({
            availability,
            pitchText,
            pitchAmount,
            pitchDeposit,
          })
        );
      }

      // this.props.history.push(
      // `/signin?referer=${encodeURIComponent(
      // `/projects/${this.props.match.params.id}`
      // )}`
      // );
      document.location = `/signin?referer=${encodeURIComponent(
        `/projects/${this.props.match.params.id}`
      )}`;
      return;
    }

    const newPitchError = {};

    // Role eligibility check disabled — see TP-207
    // const jobRole = this.getBrief().role || null;
    // if (
    //   jobRole &&
    //   !this.authUserWasInvited() &&
    //   !featuredRolesMatchJobRole(
    //     this.props.user.featured_roles || [],
    //     jobRole,
    //     this.props.suggestedRoles || []
    //   )
    // ) {
    //   newPitchError.roleEligibility =
    //     `Your featured roles do not match this job. Update your featured roles on your portfolio to apply for ${jobRole} jobs.`;
    // }

    // local validation
    if (!pitchText) {
      newPitchError.pitchText = 'Please enter a personalized application';
    } else if (pitchText.length < 100) {
      newPitchError.pitchText =
        'Please enter an application of at least 100 characters';
    }

    // location checkbox validation - for location-specific jobs only
    if (!this.getBrief().remote && !locationCheckbox) {
      newPitchError.locationCheckbox =
        'Please only pitch if you are available in this location';
    }

    // only for paid briefs
    if (this.getBrief().amount && this.getBrief().amount !== 0) {
      if (pitchAmount === '' || pitchAmount < 0 || pitchAmount === null) {
        newPitchError.pitchAmount = 'Please enter a valid price for your work';
      }

      if (pitchAmount > 0 && pitchAmount < MINIMUM_INITIAL_PITCH_AMOUNT) {
        newPitchError.pitchAmount = `Please quote a minimum ${MINIMUM_INITIAL_PITCH_AMOUNT}`;
      }

      if (!confirmationCheckbox.terms) {
        newPitchError.termsConfirmation = `Please check the above box to indicate agreement with our terms.`;
      }
    }

    if (Object.keys(newPitchError).length === 0) {
      this.props.sendPitch(
        this.props.brief,
        pitchText,
        [],
        pitchAmount,
        !pitchAmount ? null : pitchDeposit,
        null,
        availabilityToggle === 1 && availability ? availability : null,
        dayRateCurrency,
        dayRate,
        shareResumeCheckbox
      );

      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(`pitch_${this.props.match.params.id}`);
      }
    } else {
      this.setState({
        pitchError: Object.assign({}, pitchError, newPitchError),
      });
    }
  };

  isBriefExternal = () => this.getBrief().external;

  handleEditPitch = () => {
    const {
      editingPitchAmount,
      editingPitchDays,
      editingPitchDeposit,
      pitchError,
      editingPitchText,
    } = this.state;

    const newPitchError = {};

    // local validation

    if (editingPitchText.length < 10) {
      newPitchError.pitchText = 'Please enter a longer pitch';
    }

    if (Object.keys(newPitchError).length === 0) {
      // this.props.sendPitch(this.props.brief, pitchText, pitchAmount, pitchDeposit);
      // alert('save pitch');
      this.props.updatePitch(
        this.props.creativePitch.id,
        editingPitchAmount,
        editingPitchDeposit,
        editingPitchDays,
        editingPitchText
      );
    } else {
      this.setState({
        pitchError: Object.assign({}, pitchError, newPitchError),
      });
    }
  };

  handleInviteUser = (userId) => {
    this.props.inviteUser(this.props.user, userId, this.getBrief());
  };

  handleRating = (userId, briefId, value, comment = '') => {
    this.props.rate(userId, briefId, value, comment);
  };

  handleShowRelevantProjectsSelector = () => {
    if (this.props.user) {
      this.setState({ showRelevantProjectsSelector: true });
    } else {
      document.location = `/signin?referer=${encodeURIComponent(
        `/projects/${this.props.match.params.id}`
      )}`;
    }
  };

  showRatingDialog = () => {
    this.setState({
      showRatingDialog: !this.state.showRatingDialog,
    });
  };

  checkUtmCampaigns = (utmCampaign) => {
    // The following utm_campaigns mean we believe
    // they are a buyer owning this brief and not logged in
    const campaigns = [
      'user-collaboration-followup-no-pitches-1',
      'user-collaboration-pending-pitches',
      'user-collaboration-pitch-accepted-buyer',
      'user-collaboration-pitch-accepted-followup-get-update',
      'user-collaboration-pitch-accepted-followup-no-contact-buyer-b',
      'user-collaboration-pitch-accepted-followup-no-contact-buyer',
      'user-collaboration-pitch-completed-buyer',
      'user-collaboration-pitch-payment-received-confirmation',
      'user-collaboration-pitch-rate',
      'user-collaboration-public-question-posted',
      'user-new-message-simple',
    ];

    return campaigns.indexOf(utmCampaign) > -1;
  };

  handleAddVoucher = (voucherCode) => {
    this.props
      .addVoucher(this.getBrief().id, voucherCode)
      .then(() => this.props.setSuccessMessage('Voucher Added'));
  };

  handleLocationCheckboxClick = () => {
    this.setState({ locationCheckbox: !this.state.locationCheckbox });
  };

  handleShareResumeCheckboxClick = () => {
    this.setState({ shareResumeCheckbox: !this.state.shareResumeCheckbox });
  };

  handleConfirmationCheckboxClick = (type) => {
    const { confirmationCheckbox, pitchError } = this.state;

    const newConfirmationCheckbox = { ...confirmationCheckbox };
    newConfirmationCheckbox[type] = !confirmationCheckbox[type];

    if (
      newConfirmationCheckbox.terms &&
      pitchError &&
      pitchError.termsConfirmation
    ) {
      const newError = { ...pitchError };
      delete newError.termsConfirmation;
      this.setState({ pitchError: newError });
    }

    this.setState({ confirmationCheckbox: newConfirmationCheckbox });
  };

  authUserWasInvited = () =>
    this.getInvitedUserIds().includes(this.props.user.id);

  importLastPitch = async () => {
    const { loadMyPitches, myPitches, user } = this.props;
    const { pitchError } = this.state;

    if (Object.keys(myPitches).length === 0) {
      await loadMyPitches(user?.id, null, 1);
    }

    const lastPitchText = Object.values(myPitches).shift()?.text;

    if (pitchError && pitchError.pitchText) {
      const newPitchError = { ...pitchError };
      delete newPitchError.pitchText;
      this.setState({ pitchError: newPitchError });
    }

    this.setState({
      pitchText: lastPitchText?.split(
        /Related Projects:|Attached links:/gim
      )[0],
    });
  };

  getPortfolioRoles = () => {
    const { portfolio, entities, user } = this.props;
    const vanillaPortfolio = portfolio.toObject();
    const userPortfolio = vanillaPortfolio[user.username]?.toObject();
    const userCredits = userPortfolio ? userPortfolio.credits?.toJS() : [];
    const userRoleIds = [];
    const userRoles = [];
    Object.keys(entities.credits).map((creditId) => {
      const credit = entities.credits[creditId];
      if (userCredits?.includes(credit.id)) {
        credit.roles.forEach((role) => userRoleIds.push(role));
      }
    });
    Object.keys(entities.roles).map((roleId) => {
      const role = entities.roles[roleId];
      if (userRoleIds.includes(role.id)) {
        userRoles.push(role);
      }
    });
    return userRoles;
  };

  briefIsExpired = (expiry) => {
    if (moment().isAfter(expiry)) {
      return true;
    }

    return false;
  };

  onSelectProject = (id, projectLink) => {
    this.setState((prevState) => {
      const { selectedRelevantProjects, pitchText } = prevState;
      let newSelectedProjects, newPitchText;

      if (selectedRelevantProjects.includes(id)) {
        // Remove project ID and link
        newSelectedProjects = selectedRelevantProjects.filter(
          (filterId) => id !== filterId
        );

        const linkPattern = new RegExp(`\\n?${projectLink}\n`, 'g'); // Pattern to find the link in pitchText
        newPitchText = pitchText.replace(linkPattern, '');

        // Remove the title if no links are left
        if (newSelectedProjects.length === 0) {
          newPitchText = newPitchText.replace('**Related Projects:**', '');
        }

        // Clean up any leading or trailing newlines
        newPitchText = newPitchText.trim();
      } else {
        // Add project ID and link
        newSelectedProjects = [...selectedRelevantProjects, id];

        if (selectedRelevantProjects.length === 0) {
          // Adding the first link with title
          newPitchText = `${pitchText}\n\n**Related Projects:**\n\n${projectLink}\n`;
        } else {
          // Adding subsequent links
          newPitchText = `${pitchText}\n${projectLink}\n`;
        }
      }

      return {
        selectedRelevantProjects: newSelectedProjects,
        pitchText: newPitchText,
      };
    });
  };

  handleGeneratePitch = () => {
    const brief = this.getBrief();
    const owner = this.getOwner();

    const data = {
      name: this.props.user.displayname,
      featuredRoles: this.props.user.featured_roles,
      location: this.props.user.location.text,
      bio: this.props.user.bio,
      jobDescription: brief.spec,
      companyName: owner.displayname,
      type: 'pitch',
    };

    this.props.generatePitch(data);
  };

  handleBookmarkClick = () => {
    const { saveJob, unsaveJob } = this.props;
    const brief = this.getBrief();

    if (!brief || !brief.id) return;

    if (this.state.isSaved) {
      unsaveJob(brief.id);
      trackEvent('user-unsaved-job', {
        userId: this.props.user ? this.props.user.id : 'Guest',
        briefId: brief.id,
        page: 'job details',
      });
      this.setState({ isSaved: false });
    } else {
      saveJob(brief.id);
      trackEvent('user-saved-job', {
        userId: this.props.user ? this.props.user.id : 'Guest',
        briefId: brief.id,
        page: 'job details',
      });
      this.setState({ isSaved: true });
    }
  };

  render() {
    const brief = this.getBrief();
    const owner = this.getOwner();

    const {
      availability,
      availabilityToggle,
      dayRateCurrency,
      dayRate,
      isSaved,

      showCompleteAndRateConfirmation,
      showDeleteConfirmation,
      deleteReasonId,
      showPaymentConfirmation,
      showPitchSubmittedModal,
      showRatingDialog,

      pitchAmount,
      pitchDeposit,
      pitchError,
      pitchText,

      editingPitch,
      editingPitchAmount,
      editingPitchDays,
      editingPitchDeposit,
      editingPitchText,

      pitchAgain,

      selectedRelevantProjects,

      showRelevantProjectsSelector,
      askingQuestion,
    } = this.state;

    const {
      user,
      history,
      // updatingBrief,
      creativePitch,
      deletingBrief,
      entities,
      introsLeft,
      introsLoading,
      applicationCreditsRemaining,
      willUseCredits,
      loadedBrief,
      loadingGeneratedPitch,
      ownerAcceptedPitch,
      ownCredits,
      sendingPitch,
      updatingPitch,
      loadedPartners,
      partners,
    } = this.props;

    const utmCampaign = this.props.location.query.utm_campaign || null;

    // Check if the brief is deleted
    if (!brief) {
      return (
        <Grid className="mt++">
          <Row>
            <Col xs={12}>
              <Alert className="text-center" bsStyle="danger">
                {/* This brief does not exist. Check out our other{' '}
                <a href="/jobs">jobs</a>. */}
                &nbsp;
              </Alert>
            </Col>
          </Row>
        </Grid>
      );
    }

    // Some exceptions
    let briefSkill = brief.skill;

    if (briefSkill === 'Branding') {
      briefSkill = 'Brand Design';
    } else if (briefSkill === 'Copywriting') {
      briefSkill = 'Copywriter';
    }

    const metaDisplayname = owner.displayname.match(/user[0-9]+/i)
      ? 'A client'
      : owner.displayname;
    const metaTitle = brief.text + ' freelance job | Twine';
    const metaDescription =
      metaDisplayname +
      (brief.role !== ''
        ? ' is looking for a' +
          (beginsWithVowel(brief.role) ? 'n ' : ' ') +
          brief.role
        : ' needs a' +
          (beginsWithVowel(brief.role) ? 'n ' : ' ') +
          briefSkill) +
      ': ' +
      brief.text;

    const locationArray = brief.location.split(', ');
    const country =
      locationArray.length > 1 ? locationArray[1] : locationArray[0];
    const city = locationArray.length > 1 ? locationArray[0] : null;
    const locationLink = city
      ? `/jobs/${toSeoUrl(brief.role)}/in/${toSeoUrl(country)}/${toSeoUrl(
          city
        )}`
      : `/jobs/${toSeoUrl(brief.role)}/in/${toSeoUrl(country)}`;

    return (
      <div className="mt++">
        <Helmet
          title={metaTitle}
          link={[{ rel: 'canonical', href: brief.links.main }]}
          meta={[
            { name: 'description', content: metaDescription },
            { property: 'og:title', content: metaTitle },
            { property: 'og:description', content: metaDescription },
            { property: 'og:url', content: brief.links.main },
            {
              property: 'og:image',
              content: owner.avatars.images[190].square.main,
            },
            { name: 'twitter:title', content: metaTitle },
            { name: 'twitter:description', content: metaDescription },
            {
              name: 'twitter:image',
              content: owner.avatars.images[190].square.main,
            },
          ]}
        />
        <Grid>
          <Row>
            <Col xs={12}>
              <Breadcrumbs className={`mb++ ${styles.jobBreadcrumb}`}>
                {((user && user.id !== owner.id) || !user) &&
                  (brief.amount && brief.amount !== 0 ? (
                    <>
                      <Link to="/jobs" className={styles.jobBreadcrumb}>
                        Find Freelance Jobs
                      </Link>
                      <span className="ml mr">&gt;</span>
                      <Link
                        to={`/jobs/${toSeoUrl(brief.role)}`}
                        className={styles.jobBreadcrumb}
                      >
                        {brief.role} Jobs
                      </Link>
                      {brief.location && (
                        <>
                          <span className="ml mr">&gt;</span>
                          <Link
                            to={locationLink}
                            className={styles.jobBreadcrumb}
                          >
                            {brief.role} Jobs in {city ?? country}
                          </Link>
                        </>
                      )}
                    </>
                  ) : (
                    <Link to="/collaborate">Collaborate</Link>
                  ))}
                {user && user.id === owner.id && (
                  <Link to="/ats">Manage Jobs</Link>
                )}
                Job Details
              </Breadcrumbs>
            </Col>
          </Row>
          {!user && utmCampaign && this.checkUtmCampaigns(utmCampaign) && (
            <Alert
              bsStyle="success"
              className="text-center lato-regular fs-18 pl pr"
            >
              <span className="mr ml">Is this your project?</span>
              <Button
                bsStyle="success"
                bsSize="lg"
                href="/signin"
                className="lato-bold"
              >
                Sign in to see your pitches
              </Button>
            </Alert>
          )}
          <Row>
            <Col lg={5} xsHidden={user && owner && user.id === owner.id}>
              {(!user || user.id !== owner.id) && (
                <BriefDetail
                  brief={brief}
                  owner={owner}
                  isProUser={user && user.pro ? user.pro : false}
                  user={this.props.user}
                  isExpired={this.briefIsExpired(brief.expires_at)}
                  isExternal={this.isBriefExternal()}
                  onBookmarkClick={this.handleBookmarkClick}
                  isSaved={isSaved}
                />
              )}
              <br />
              {ownerAcceptedPitch && (
                <div>
                  <strong className="mt">Hired:</strong>
                  <br />
                  <br />
                  <Pitch
                    brief={brief}
                    pitch={ownerAcceptedPitch}
                    acceptHireAB={this.getAcceptHireAB()}
                    paymentVerificationRequired={
                      ownerAcceptedPitch.deposit > 0
                        ? false
                        : this.state.paymentVerificationRequired
                    }
                  />
                </div>
              )}
              {this.isBriefRelevant() &&
                !this.isBriefExternal() &&
                user &&
                owner.id !== user.id &&
                (!creativePitch || creativePitch.accepted_at === '') &&
                (!creativePitch || pitchAgain) && (
                  <PublicQuestions
                    askingQuestion={askingQuestion}
                    onAskQuestion={(askingQuestion) =>
                      this.setState({ askingQuestion })
                    }
                    questions={this.props.questions.map(
                      (id) => this.props.publicQuestionEntities[id]
                    )}
                    ownQuestions={this.props.ownQuestions.map(
                      (id) => this.props.publicQuestionEntities[id]
                    )}
                    loadingQuestions={this.props.loadingQuestions}
                    loadedQuestions={this.props.loadedQuestions}
                    moreQuestions={this.props.moreQuestions}
                    onLoadMore={() =>
                      this.props.loadQuestions(
                        this.props.brief,
                        user && owner && user.id === owner.id ? null : false,
                        this.props.questionPagesLoaded + 1
                      )
                    }
                    postingQuestion={this.props.postingQuestion}
                    questionText={this.props.questionText}
                    onUpdateQuestionText={(event) =>
                      this.props.updateQuestionText(event.target.value)
                    }
                    onPostQuestion={() =>
                      this.props.postQuestion(
                        this.props.brief,
                        this.props.questionText
                      )
                    }
                    loggedIn={!!user}
                    isOwner={user && owner && user.id === owner.id}
                    onEdit={(id) =>
                      this.props.pushState(
                        this.props.location.pathname + '/questions/' + id
                      )
                    }
                  />
                )}
            </Col>
            <Col lg={7} id="jobDetailsPitchSection">
              {!this.isPageLoaded() &&
                !(user && user.id === owner.id && loadedBrief) && (
                  <div className="mt">
                    <Loader loading />
                  </div>
                )}
              {this.isPageLoaded() && !this.isBriefRelevant() && (
                <ExpiredBriefDetailsSignUp
                  briefRole={
                    this.getBrief().role === ''
                      ? this.getBrief().skill
                      : this.getBrief().role
                  }
                  loggedIn={user ? true : false}
                />
              )}
              {!user && this.isBriefRelevant() && (
                <BriefDetailsSignUp
                  isExternal={this.isBriefExternal()}
                  popularityWarning={brief && brief.num_of_pitches >= 1}
                />
              )}
              <div>
                {/* Role mismatch API error — shown when the API rejects a pitch
                    that somehow bypassed the frontend eligibility check */}
                {this.props.sendPitchRoleMismatchError && (
                  <Alert bsStyle="danger">
                    {this.props.sendPitchRoleMismatchError}{' '}
                    <a
                      href={`${
                        user && user.links && user.links.main_relative
                      }/about#portfolio-roles`}
                    >
                      Update your featured roles
                    </a>
                  </Alert>
                )}

                {/* Freelancer pitch box */}
                {this.isBriefRelevant() &&
                  !this.isBriefExternal() &&
                  user &&
                  owner.id !== user.id &&
                  (!creativePitch || creativePitch.accepted_at === '') &&
                  (!creativePitch || pitchAgain) && (
                    <CreativePitch
                      availability={availability}
                      availabilityToggle={availabilityToggle}
                      authUserWasInvited={this.authUserWasInvited()}
                      briefCurrency={this.getBrief().currency || ''}
                      briefId={this.getBrief().id}
                      jobRole={this.getBrief().role || null}
                      briefRole={
                        this.getBrief().role === ''
                          ? this.getBrief().skill
                          : this.getBrief().role
                      }
                      suggestedRoles={this.props.suggestedRoles || []}
                      loadingSuggestedRoles={
                        this.props.loadingSuggestedRoles || false
                      }
                      briefPaid={
                        this.getBrief().amount && this.getBrief().amount !== 0
                      }
                      briefRemote={this.getBrief().remote}
                      briefLocation={this.getBrief().location}
                      briefNoPitchLimits={
                        this.getBrief().no_pitch_limits || false
                      }
                      locationCheckbox={this.state.locationCheckbox}
                      onLocationCheckboxClick={this.handleLocationCheckboxClick}
                      shareResumeCheckbox={this.state.shareResumeCheckbox}
                      onShareResumeCheckboxClick={
                        this.handleShareResumeCheckboxClick
                      }
                      onConfirmationCheckboxClick={
                        this.handleConfirmationCheckboxClick
                      }
                      creativeJob={isCreativeJob(
                        this.getBrief().role === ''
                          ? this.getBrief().skill
                          : this.getBrief().role
                      )}
                      creditsRoles={this.getPortfolioRoles()}
                      dayRate={dayRate}
                      dayRateCurrency={dayRateCurrency}
                      depositAmounts={this.getDepositAmounts()}
                      key={1}
                      loadingPitch={false}
                      introsLeft={introsLeft}
                      introsLoading={introsLoading}
                      applicationCreditsRemaining={applicationCreditsRemaining}
                      willUseCredits={willUseCredits}
                      pitchAmount={pitchAmount ? pitchAmount : ''}
                      // pitchDays={pitchDays}
                      pitchDeposit={pitchDeposit}
                      pitchError={pitchError}
                      pitchText={pitchText}
                      onChangeAvailability={(availability) =>
                        this.setState({ availability })
                      }
                      onChangeAvailabilityToggle={(availabilityToggle) =>
                        this.setState({ availabilityToggle })
                      }
                      onChangeDayRate={(dayRate) => this.setState({ dayRate })}
                      onChangeDayRateCurrency={(dayRateCurrency) =>
                        this.setState({ dayRateCurrency })
                      }
                      onChangePitchAmount={(pitchAmount) =>
                        this.setState({
                          pitchAmount,
                          pitchError: Object.assign({}, pitchError, {
                            pitchAmount: null,
                          }),
                        })
                      }
                      // onChangePitchDays={pitchDays => this.setState({ pitchDays })}
                      onChangePitchDeposit={(pitchDeposit) =>
                        this.setState({
                          pitchDeposit,
                          pitchError: Object.assign({}, pitchError, {
                            pitchDeposit: null,
                          }),
                        })
                      }
                      onChangePitchText={(pitchText) =>
                        this.setState({
                          pitchText,
                          pitchError: Object.assign({}, pitchError, {
                            pitchText: null,
                          }),
                        })
                      }
                      loadingGeneratedPitch={loadingGeneratedPitch}
                      onGeneratePitch={() => this.handleGeneratePitch()}
                      onImportLastPitch={() => this.importLastPitch()}
                      onShowRelevantProjectsSelector={() =>
                        this.handleShowRelevantProjectsSelector()
                      }
                      onSubmitPitch={() => this.handleSendPitch()}
                      relevantProjects={selectedRelevantProjects.map(
                        (id) => entities.projects[id]
                      )}
                      submittedPitch={creativePitch}
                      submittingPitch={sendingPitch}
                      totalPitches={
                        this.getBrief().num_of_pitches_or_pending_pitches
                      }
                      user={user}
                      onAttachUpload={() =>
                        this.setState({ showRelevantProjectsSelector: true })
                      }
                    />
                  )}
                {/* Freelancer sent a pitch */}
                {this.isBriefRelevant() &&
                  !this.isBriefExternal() &&
                  (!user ||
                    (owner.id !== user.id &&
                      (!creativePitch || creativePitch.accepted_at === ''))) &&
                  creativePitch &&
                  !pitchAgain && (
                    <CreativeSubmittedPitch
                      authUserWasInvited={this.authUserWasInvited()}
                      briefRole={
                        this.getBrief().role === ''
                          ? this.getBrief().skill
                          : this.getBrief().role
                      }
                      isPaidBrief={
                        this.getBrief().amount && this.getBrief().amount !== 0
                      }
                      currency={this.getBrief().currency || 'usd'}
                      contactOptions={owner.contact_options}
                      depositAmounts={this.getDepositAmounts()}
                      shouldOpenEditing={this.props.location.query.editPitch}
                      editing={editingPitch}
                      editingPitchAmount={editingPitchAmount}
                      editingPitchDays={editingPitchDays}
                      editingPitchDeposit={editingPitchDeposit}
                      editingPitchText={editingPitchText}
                      onChangePitchAmount={(editingPitchAmount) =>
                        this.setState({
                          editingPitchAmount,
                          pitchError: Object.assign({}, pitchError, {
                            pitchAmount: null,
                          }),
                        })
                      }
                      onChangePitchDays={(editingPitchDays) =>
                        this.setState({ editingPitchDays })
                      }
                      onChangePitchDeposit={(editingPitchDeposit) =>
                        this.setState({
                          editingPitchDeposit,
                          pitchError: Object.assign({}, pitchError, {
                            pitchDeposit: null,
                          }),
                        })
                      }
                      onChangePitchText={(editingPitchText) =>
                        this.setState({
                          editingPitchText,
                          pitchError: Object.assign({}, pitchError, {
                            pitchText: null,
                          }),
                        })
                      }
                      onEdit={() =>
                        this.setState({
                          editingPitch: true,
                          editingPitchAmount: creativePitch.budget / 100,
                          editingPitchDeposit: creativePitch.deposit,
                          editingPitchText: creativePitch.text,
                          pitchError: {},
                          editingPitchDays: creativePitch.days_to_complete
                            ? creativePitch.days_to_complete
                            : null,
                          editingPitchAvailability: creativePitch.available_at,
                          editingDayRate: creativePitch.day_rate,
                          editingDayRateCurrency:
                            creativePitch.day_rate_currency,
                        })
                      }
                      onEditSave={() => this.handleEditPitch()}
                      onEditCancel={() =>
                        this.setState({ editingPitch: false })
                      }
                      onPitchAgain={() =>
                        this.setState({
                          pitchAgain: true,
                          pitchText: creativePitch.text,
                          pitchAmount: creativePitch.budget / 100,
                          pitchDeposit: creativePitch.deposit,
                          pitchDays: creativePitch.days_to_complete
                            ? creativePitch.days_to_complete
                            : null,
                        })
                      }
                      onResendContactDetails={() =>
                        this.handleResendContactDetails()
                      }
                      owner={owner}
                      pitch={creativePitch}
                      pitchError={pitchError}
                      savingEdit={updatingPitch}
                      user={user}
                    />
                  )}
                {/* You've been hired - pitchState.accepted */}
                {this.isBriefRelevant() &&
                  !this.isBriefExternal() &&
                  creativePitch &&
                  creativePitch.accepted_at !== '' &&
                  creativePitch.completed_at === '' && (
                    <div className={styles.hiredContainer}>
                      <div className={styles.hiredTitle}>
                        {"You've been hired!"}
                      </div>
                      <p className={styles.line}>
                        <span>Discuss the job with your client</span>

                        <span className={`ml- ${styles.messageClient}`}>
                          <span className={styles.messageIcon}>
                            <SvgIcon icon="bubble" color="#40a0f5" size="14" />
                          </span>
                          <Link
                            to={`/messages/${
                              entities.users[Object.keys(entities.users)[0]]
                                .username
                            }`}
                            className={styles.messageLabel}
                          >
                            Messages
                          </Link>
                        </span>
                      </p>

                      <p className={styles.line}>
                        <span>Request a payment from the client</span>
                        <Button
                          bsStyle="success"
                          className={styles.newBillButton}
                          href={`/manage/pitches/${creativePitch.id}/new-bill`}
                        >
                          New Bill
                        </Button>
                      </p>
                    </div>
                  )}

                {/* The client has ended this hire. - pitchState.completed */}
                {this.isBriefRelevant() &&
                  !this.isBriefExternal() &&
                  creativePitch &&
                  creativePitch.accepted_at !== '' &&
                  creativePitch.completed_at !== '' && (
                    <div className={styles.hiredContainer}>
                      <div className={styles.hiredTitle}>
                        The client has ended this hire.
                      </div>
                      <p className={styles.line}>
                        <span>
                          Please contact them to re-hire you if you have further
                          bills.
                        </span>

                        <span className={`ml- ${styles.messageClient}`}>
                          <span className={styles.messageIcon}>
                            <SvgIcon icon="bubble" color="#40a0f5" size="14" />
                          </span>
                          <Link
                            to={`/messages/${
                              entities.users[Object.keys(entities.users)[0]]
                                .username
                            }`}
                            className={styles.messageLabel}
                          >
                            Messages
                          </Link>
                        </span>
                      </p>
                    </div>
                  )}

                {/* You've been hired - unsure if used?  */}
                {this.isBriefRelevant() &&
                  !this.isBriefExternal() &&
                  creativePitch &&
                  creativePitch.accepted_at !== '' &&
                  !(brief.amount || 0) && (
                    <CreativeAcceptedPitch
                      paid={brief.amount && brief.amount !== 0 ? true : false}
                      pitch={creativePitch}
                      sentFinalFiles={false}
                      onSendFiles={() => {}}
                    />
                  )}
              </div>

              {/* External job */}
              {this.isBriefExternal() &&
                user &&
                !this.briefIsExpired(brief.expires_at) && (
                  <Panel className={styles.externalBox}>
                    <div className={styles.externalTitle}>
                      <div className={styles.externalLabel}>
                        External Job
                        <Glyphicon
                          className={styles.externalGlyphIcon}
                          glyph="share"
                        />
                      </div>
                    </div>
                    <div className={styles.externalCopy}>
                      <p>
                        External jobs are hosted and applied for outside of
                        Twine.
                      </p>
                      <p className="aside-text">
                        Source: {new URL(this.getBrief().external_url).hostname}
                      </p>
                    </div>
                    <div
                      className={styles.externalButtonBlock}
                      onClick={() => trackEvent('external-job-click-apply')}
                    >
                      <div>
                        <Button
                          rel="nofollow"
                          bsStyle="primary"
                          bsSize="large"
                          href={this.getBrief().external_url}
                          target="blank"
                        >
                          <span>Apply</span>{' '}
                          <Glyphicon
                            className={styles.externalGlyphIcon}
                            glyph="share"
                          />
                        </Button>
                      </div>
                    </div>
                    <div className={styles.applicationWriterUpsell}>
                      <p className="mt">
                        <strong>Tired of writing cover letters?</strong>
                      </p>
                      <p>
                        {
                          "Use Twine's AI writer ✨ for cover letters, proposals and pitches."
                        }{' '}
                        <a href="/freelance/cover-letter-proposal-writing-tool">
                          Try it out
                        </a>
                      </p>
                    </div>
                  </Panel>
                )}

              {/* External job - partner ads */}
              {(this.isBriefExternal() ||
                this.briefIsExpired(brief.expires_at)) &&
                loadedPartners && (
                  <div>
                    <PortfolioAdverts
                      loadedPartners={loadedPartners}
                      partners={partners}
                      userLocation={user?.location?.text ?? null}
                    />
                  </div>
                )}

              {/* Guest - How it works */}
              {!user && <BriefDetailsHowItWorks />}
              <RelatedJobs
                briefId={this.getBrief().id}
                role={this.getBrief().role}
                skill={this.getBrief().skill}
                user={user}
                country={country}
                city={city}
                locationWithRoleLink={locationLink}
              />
            </Col>
          </Row>
        </Grid>
        <Modal
          animation="scale"
          show={showDeleteConfirmation}
          onExit={() =>
            this.setState({ showDeleteConfirmation: false, deleteReasonId: 0 })
          }
        >
          <div className="text-center" style={{ fontSize: '500%', margin: 0 }}>
            <Icon color="#f0ad4e" icon="&#xe632;" />
          </div>
          <p className="text-center">
            Deleting this brief will decline all pitches
          </p>
          <p className="text-center mt++ mb">
            Please tell us why you are deleting the brief
          </p>
          <div className="center" style={{ display: 'table' }}>
            {this.getDeleteReasons().map((reason, key) => {
              return (
                <Radio
                  key={'delete_reason_' + key}
                  onChange={() => this.handleDeleteReasonChange(key + 1)}
                  checked={deleteReasonId === key + 1}
                >
                  {reason}
                </Radio>
              );
            })}
          </div>
          <p className="text-center mt++">
            <Button
              disabled={deleteReasonId === 0 || deletingBrief}
              style={{ marginRight: '10px' }}
              bsStyle="warning"
              onClick={() => this.handleDeleteBrief(true)}
            >
              {deletingBrief ? 'Deleting...' : 'Delete'}
            </Button>
            <Button
              disabled={deletingBrief}
              style={{ marginLeft: '10px' }}
              bsStyle="info"
              onClick={() =>
                this.setState({
                  showDeleteConfirmation: false,
                  deleteReasonId: 0,
                })
              }
            >
              Cancel
            </Button>
          </p>
        </Modal>
        <AlertBox
          confirmButtonText="Confirm"
          icon="success"
          onCancel={() => this.setState({ showPaymentConfirmation: false })}
          onConfirm={() =>
            history.push(
              `/manage/briefs/pay/${brief.id}/${ownerAcceptedPitch.id}`
            )
          }
          show={showPaymentConfirmation}
          text="Are you ready to send payment for this project?"
          theme="success"
        />
        {ownerAcceptedPitch && (
          <Modal
            show={showCompleteAndRateConfirmation}
            onExit={() =>
              this.setState({ showCompleteAndRateConfirmation: false })
            }
          >
            <div style={{ textAlign: 'center', fontSize: '700%', margin: 0 }}>
              <Icon color="#0fe2af" icon="&#xe632;" />
            </div>
            <p style={{ textAlign: 'center', fontSize: '200%' }}>
              <strong>Complete and rate?</strong>
            </p>
            <p style={{ textAlign: 'center', fontSize: '130%' }}>
              Are you ready to mark this project as complete and rate your
              creative?
            </p>
            <p className="text-center mt">
              <Button
                style={{ marginRight: 50 }}
                bsSize="lg"
                bsStyle="info"
                onClick={() =>
                  this.setState({ showCompleteAndRateConfirmation: false })
                }
              >
                Cancel
              </Button>
              <Button
                style={{ marginLeft: 50 }}
                bsSize="lg"
                bsStyle="success"
                href={
                  '/manage/briefs/thankyou/' +
                  brief.id +
                  '/' +
                  ownerAcceptedPitch.id
                }
              >
                Confirm
              </Button>
            </p>
          </Modal>
        )}

        <AlertBox
          icon="success"
          onConfirm={() => this.setState({ showPitchSubmittedModal: false })}
          show={showPitchSubmittedModal}
          title="Your application has been sent and is awaiting review"
          theme="success"
        />

        {showRatingDialog && (
          <RatingModal
            pitch={ownerAcceptedPitch}
            brief={brief}
            user={ownerAcceptedPitch.sender}
            onRated={this.handleRating}
            onExit={() => this.setState({ showRatingDialog: false })}
            subtitle={
              'How was it working with ' +
              ownerAcceptedPitch.sender.displayname +
              '? Leave a rating now:'
            }
          />
        )}
        <Modal
          onEnter={() => {
            if (
              this.props.user &&
              !this.props.ownCredits.loaded &&
              !this.props.ownCredits.loading
            ) {
              this.props.loadOwnCredits(this.props.user.id);
            }
          }}
          show={showRelevantProjectsSelector}
          title="Select relevant examples"
          closeButton
          onExit={() => this.setState({ showRelevantProjectsSelector: false })}
        >
          <ProjectSelector
            loadingProjects={ownCredits.loading}
            onSelectProject={this.onSelectProject}
            projects={
              ownCredits.credits.length > 0 &&
              typeof ownCredits.credits[0] !== 'undefined'
                ? ownCredits.credits.map(
                    (id) => entities.projects[entities.credits[id].project]
                  )
                : []
            }
            selectedProjects={selectedRelevantProjects}
          />
          {!ownCredits.loading && ownCredits.more && (
            <Button
              className={styles.loadMoreRelevantProjects}
              bsSize="sm"
              bsStyle="info"
              onClick={() =>
                this.props.loadOwnCredits(user.id, ownCredits.pagesLoaded + 1)
              }
            >
              Load more
            </Button>
          )}
          <div className={styles.projectSelectorBtnContainer}>
            <Button
              bsStyle="primary"
              className={styles.projectSelectorBtn}
              onClick={() =>
                this.setState({ showRelevantProjectsSelector: false })
              }
            >
              <div className={styles.selectorCopyContainer}>
                <span className={styles.selectorCopy}>Attach </span>
                <SvgIcon icon="Paper_clip_font_awesome" size="20" />
              </div>
            </Button>
          </div>
        </Modal>
      </div>
    );
  }
}
